<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import clone from 'clone'
import { isEqual } from 'lodash-es'

import { useQuoter } from '~/layers/forms/composables/useQuoter'
import type { Contract } from '~/layers/forms/models'
import { Coverage, Form } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

import type { CoverageListExpose } from '../../types/coverageComparatorDefinedExpose'

interface Props {
  modelValue: Contract
  insurable: Insurable
  isUserChosing?: boolean
}

const props = defineProps<Props>()

const emits = defineEmits(['update:modelValue', 'selected'])
const model = useVModel(props, 'modelValue', emits)
const { RemoteConfigurationStore } = stores()

const v$ = useValidation(
  () => ({
    argentoInsuranceProductId: { required: requiredValidator },
  }),
  computed(() => model.value.coverage),
  {
    $scope: Form.coverage,
    $registerAs: Form.coverage,
  }
)

const requireCriteria = computed(
  () =>
    RemoteConfigurationStore?.value?.ramoEnum &&
    ['Auto', 'Viajes'].includes(RemoteConfigurationStore.value.ramoEnum)
)

const selectedCoverage = computed(() => model.value.coverage.argentoInsuranceId)

const { quote, quotationStatus, applicableStaticCoverages } = useQuoter({
  insurable: computed(() => props.insurable),
  periodicity: Coverage.getPeriodicityList().default?.value,
  inmediate: !requireCriteria.value,
})

const selected = async ({ data, pasive }: { data: Coverage; pasive?: boolean }) => {
  model.value.coverage = clone(data)
  if (!(await v$.value.$validate())) return
  if (!pasive) emits('selected')
}

watch(
  computed(() => props.insurable.quotationParams),
  async (n, o) => {
    if (isEqual(n, o)) return
    if (
      (n?.completedInitialParams && props.isUserChosing) ||
      (n?.completedFinalParams && !props.isUserChosing)
    ) {
      await quote()
    }

    if (!n?.completedFinalParams && !selectedCoverage.value) return

    const selectedCoverageData = applicableStaticCoverages.value?.find(
      (c) => c.argentoInsuranceId === selectedCoverage.value
    )

    if (selectedCoverageData) selected({ data: selectedCoverageData, pasive: true })
  },
  {
    deep: true,
    immediate: true,
  }
)

watch(
  () => props.isUserChosing,
  (n, o) => {
    if (!o && Boolean(n)) model.value.coverage = Coverage.load()
  }
)

defineExpose<CoverageListExpose>({
  getQuotationsStatus: () => quotationStatus.value,
})
</script>

<template>
  <div
    :id="`${Form.coverage}-form`"
    class="flex flex-col gap-8"
  >
    <FormsCoverageCriteria
      v-if="requireCriteria"
      v-model="model"
    />
    <div class="flex flex-col gap-4">
      <FormsGlobalTitle
        text="Seleccione una cobertura"
        icon-class="icon-[ph--shield-check-bold]"
      />
      <ClientOnly>
        <ProcessingComponent
          v-if="quotationStatus === 'pending'"
          loading-message="Consultando planes..."
        />
        <template v-else-if="!applicableStaticCoverages?.length">
          <PvMessage
            severity="warn"
            class="flex flex-col justify-center"
          >
            <div class="p-3 text-center">
              <template
                v-if="requireCriteria && !insurable.quotationParams?.completedInitialParams"
              >
                <div class="p-3 text-center">
                  <div>
                    <span class="icon-[ph--info-bold] text-3xl" />
                  </div>
                  <div class="text-md">Complete los datos anteriores para cotizar su producto.</div>
                </div>
              </template>
              <template v-else-if="quotationStatus === 'error'">
                <div class="flex flex-col gap-2 items-center">
                  <div class="text-center">
                    Hubo un problema al intentar obtener las coberturas
                  </div>
                  <PvButton
                    severity="contrast"
                    outlined
                    class="w-full"
                    @click="quote()"
                  >
                    <span class="icon-[ph--arrow-counter-clockwise-bold]" />
                    Reintentar
                  </PvButton>
                </div>
              </template>
              <template v-else>
                <div class="text-center"> No se encontraron coberturas para esta consulta </div>
              </template>
            </div>
          </PvMessage>
        </template>
        <GridComponent
          v-show="quotationStatus === 'success' && applicableStaticCoverages?.length"
          :cols="(applicableStaticCoverages?.length ?? 1) > 2 ? 3 : 2"
          :min-cols="1"
        >
          <template
            v-for="plan in applicableStaticCoverages"
            :key="plan.argentoInsuranceId?.toString()"
          >
            <FormsCoverageItem
              :plan="plan"
              :quotation-status="quotationStatus"
              @selected="selected"
            />
          </template>
        </GridComponent>
      </ClientOnly>
    </div>
  </div>
</template>
